import { useEffect } from 'react'
import { trackCalendlyWidgetLoad, trackCalendlyBooking } from '@/lib/analytics'

interface CalendlyWidgetProps {
  calendlyUrl: string
}

// The Calendly embed script attaches a global at `window.Calendly` once loaded.
// We only use `initInlineWidget`. A dependency's types already declare this global
// as `any`, and global augmentations must agree, so we match that here; reads are
// still funneled through the `isCalendlyGlobal` guard below rather than used raw.
interface CalendlyGlobal {
  initInlineWidget(options: { url: string; parentElement: HTMLElement }): void
}

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- must match a dependency's `any` global augmentation
    Calendly?: any
  }
}

function isCalendlyGlobal(value: unknown): value is CalendlyGlobal {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { initInlineWidget?: unknown }).initInlineWidget === 'function'
  )
}

export function CalendlyWidget({ calendlyUrl }: CalendlyWidgetProps) {
  useEffect(() => {
    // Load Calendly script
    const script = document.createElement('script')
    script.src = 'https://assets.calendly.com/assets/external/widget.js'
    script.async = true
    document.body.appendChild(script)

    // Load Calendly CSS
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = 'https://assets.calendly.com/assets/external/widget.css'
    document.head.appendChild(link)

    return () => {
      document.body.removeChild(script)
      document.head.removeChild(link)
    }
  }, [])

  // Listen for Calendly embed events (booking completed, event selected, etc.)
  useEffect(() => {
    const handleMessage = (e: MessageEvent) => {
      if (e.data?.event === 'calendly.event_scheduled') {
        trackCalendlyBooking()
        // Notify webhook Lambda (best-effort, no Calendly paid plan needed)
        fetch('https://cezz6d40ld.execute-api.us-west-1.amazonaws.com/calendly', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            event: 'invitee.created',
            payload: {
              email: e.data?.payload?.invitee?.email ?? '',
              name: e.data?.payload?.invitee?.name ?? '',
              status: 'active',
              scheduled_event: {
                uri: e.data?.payload?.event?.uri ?? '',
                name: 'Strategy Call',
                start_time: '',
                end_time: '',
              },
            },
          }),
        }).catch(() => {}) // Fire and forget
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [])

  useEffect(() => {
    const initCalendly = () => {
      const container = document.getElementById('calendly-inline-widget')
      const candidate: unknown = window.Calendly
      if (isCalendlyGlobal(candidate) && container) {
        candidate.initInlineWidget({
          url: calendlyUrl,
          parentElement: container,
        })
        trackCalendlyWidgetLoad()
      }
    }

    // Wait for script to load
    const checkCalendly = setInterval(() => {
      if (isCalendlyGlobal(window.Calendly)) {
        initCalendly()
        clearInterval(checkCalendly)
      }
    }, 100)

    return () => clearInterval(checkCalendly)
  }, [calendlyUrl])

  return <div id="calendly-inline-widget" className="h-[700px] min-w-[320px]" data-auto-load="false" />
}
